import { Search as SearchIcon } from 'components/icons';
import { useInRecordSection, useSectionWideClass } from 'components/section-edit/SectionEdit';
import { Button } from 'components/ui/button';
import React, { useState } from 'react';
import { cn } from 'utils/cn';
import { FieldValues, useWatch } from 'react-hook-form';
import { MinDto } from 'types/other.ts';
import { useModal } from '../../../modals/useModal.ts';
import DynamicMultiSelectInput, {
    Props as DynamicMultiSelectInputProps,
} from '../../inputs/rhf-inputs/DynamicMultiSelectInput';
import { pickerButtonName } from './DynamicSelectPicker';

export type PickerModalProps<TPickedOption, TCreatedOption> = {
    onClose: () => void;
    onConfirm: (selectedItem: TPickedOption) => void;
    onCreateNew: (selectedItem: TCreatedOption) => void;
    searchValue: string;
    currentValue?: TPickedOption[];
    paginatedFilter?: string;
    isOptionEnabled?: (option: TPickedOption) => boolean;
};

type SelectPickerProps<
    TFormData extends FieldValues,
    TOption extends FieldValues,
    TPickedOption,
    TCreatedOption,
    TModalProps extends PickerModalProps<TPickedOption, TCreatedOption> = PickerModalProps<
        TPickedOption,
        TCreatedOption
    >,
> = DynamicMultiSelectInputProps<TFormData, TOption> & {
    PickerModalComponent: React.ComponentType<TModalProps>;
    isPickerModalOptionEnabled?: (option: TPickedOption) => boolean;
};

const DynamicMultiSelectPicker = <
    TFormData extends FieldValues,
    TOption extends FieldValues,
    TPickedOption extends MinDto,
    TCreatedOption extends MinDto,
>(
    props: SelectPickerProps<TFormData, TOption, TPickedOption, TCreatedOption>
) => {
    const { PickerModalComponent } = props;
    const { openModal, closeModal } = useModal();
    const [inputValue, setInputValue] = useState('');
    const currentValue = useWatch({ control: props.control, name: props.name });
    const inSection = useInRecordSection();
    const wideClass = useSectionWideClass(props.control, props.name);
    // In a record section the button sits on the label's line, in the room a multi-select's label leaves at its
    // end, so the chips have the width of the read view's row and wrap onto the same lines. It stays after the
    // field in the document, and in the tab order.
    const isButtonOnLabelLine = inSection && !!props.label;

    const handleConfirm = (selectedItem: TPickedOption | TCreatedOption) => {
        const filteredValue = (currentValue || []).filter((item: MinDto) => item.id !== selectedItem.id);
        const newValue = [...filteredValue, selectedItem];
        props.setValue(props.name, newValue);
        closeModal();
        setInputValue('');
    };

    const handleOpenModal = () => {
        openModal(
            <PickerModalComponent
                onClose={closeModal}
                onConfirm={handleConfirm}
                onCreateNew={handleConfirm}
                searchValue={inputValue}
                currentValue={currentValue}
                isOptionEnabled={props.isPickerModalOptionEnabled}
            />,
            closeModal,
            { width: '90vw', maxWidth: '1200px' }
        );
    };

    const handleSearchChange = (eventType: string, searchValue: string) => {
        if (eventType !== 'blur') {
            setInputValue(searchValue);
        }
    };

    return (
        // In a record section the field and its button are one cell of the fields grid, which `form-field` says,
        // with a multi-select's span.
        <div
            className={cn(
                'flex w-full items-start gap-2',
                inSection && 'form-field',
                wideClass,
                isButtonOnLabelLine && 'relative'
            )}
        >
            <div className="min-w-0 flex-1">
                <DynamicMultiSelectInput {...props} onSearchChange={handleSearchChange} />
            </div>
            {!props.readonly && (
                <Button
                    type="button"
                    variant="ghost"
                    size={isButtonOnLabelLine ? 'icon-xs' : 'icon'}
                    onClick={handleOpenModal}
                    disabled={props.disabled}
                    className={cn(
                        'text-primary',
                        isButtonOnLabelLine
                            ? // Centred on the label's 16px line.
                              'absolute -top-1 right-0'
                            : // Top-aligned and pushed past the label row (28px with its gap), so it sits level
                              // with the 36px field and stays there when an error line renders under the field.
                              props.label && 'mt-7'
                    )}
                    aria-label={pickerButtonName(props)}
                >
                    <SearchIcon className={isButtonOnLabelLine ? 'size-4' : 'size-5'} />
                </Button>
            )}
        </div>
    );
};

export default DynamicMultiSelectPicker;
